import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import type {
  LoginRequest,
  PublicUser,
  RegisterRequest,
  TotpVerifyRequest,
} from '@smartapply/shared';

import {
  authLogin,
  authLogout,
  authMe,
  authRegister,
  totpVerify,
  SmartApplyApiError,
} from './api-client.js';

/**
 * Resultado do login: ou autentica direto, ou pede 2FA.
 */
export type LoginResult =
  | { kind: 'authenticated'; user: PublicUser }
  | { kind: 'requires2fa'; challengeToken: string; expiresAt: string };

export interface AuthContextValue {
  /** undefined = ainda carregando; null = não logado; user = logado. */
  user: PublicUser | null | undefined;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (payload: LoginRequest) => Promise<LoginResult>;
  verifyTotp: (payload: TotpVerifyRequest) => Promise<PublicUser>;
  register: (payload: RegisterRequest) => Promise<PublicUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await authMe();
      setUser(result?.user ?? null);
    } catch (err) {
      if (err instanceof SmartApplyApiError && err.status === 401) {
        setUser(null);
      } else {
        setUser((u) => (u === undefined ? null : u));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (payload: LoginRequest): Promise<LoginResult> => {
    const res = await authLogin(payload);
    if (res.requires2fa) {
      return {
        kind: 'requires2fa',
        challengeToken: res.challengeToken,
        expiresAt: res.expiresAt,
      };
    }
    setUser(res.user);
    return { kind: 'authenticated', user: res.user };
  }, []);

  const verifyTotp = useCallback(async (payload: TotpVerifyRequest) => {
    const { user: u } = await totpVerify(payload);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (payload: RegisterRequest) => {
    const { user: u } = await authRegister(payload);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await authLogout();
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    refresh,
    login,
    verifyTotp,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth: AuthProvider ausente acima na árvore.');
  return ctx;
}
